import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { UserPicker } from '../../components/UserPicker.js';

interface ControlRow {
  readonly controlId: string;
  readonly controlCode: string;
  readonly controlName: string;
  readonly controlType: string;
  readonly testingFrequency: string;
  readonly effectiveness: string;
}

export function M21ControlTestingPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ControlRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [controlCode, setControlCode] = useState('');
  const [controlName, setControlName] = useState('');
  const [controlType, setControlType] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [testingFrequency, setTestingFrequency] = useState('QUARTERLY');

  function load(): void {
    apiRequest<{ data: ControlRow[] }>('/v1/controls', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: ControlRow[] }>('/v1/controls', { token })
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setActionError(null);
    if (!controlCode.trim() || !controlName.trim() || !controlType.trim() || !ownerId.trim()) {
      setActionError(t('m21.controlTesting.validationRequired'));
      return;
    }
    setBusyId('create');
    try {
      await apiRequest('/v1/controls', {
        method: 'POST',
        token,
        body: {
          controlCode: controlCode.trim(),
          controlName: controlName.trim(),
          controlType: controlType.trim(),
          ownerId: ownerId.trim(),
          testingFrequency,
        },
      });
      setControlCode(''); setControlName(''); setControlType('');
      setOwnerId(''); setTestingFrequency('QUARTERLY');
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function recordTest(id: string, testResult: string): Promise<void> {
    setActionError(null);
    setBusyId(`test-${id}`);
    try {
      await apiRequest(`/v1/controls/${id}/test`, {
        method: 'POST',
        token,
        body: { testDate: new Date().toISOString().slice(0, 10), testResult },
      });
      load();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m21.controlTesting.loading')}</p>;
  return (
    <main aria-label="control-testing">
      <h1>{t('m21.controlTesting.title')}</h1>

      <form aria-label="control-create-form" onSubmit={handleCreate}>
        <label>{t('m21.controlTesting.formCode')}<input aria-label="Control code" value={controlCode} onChange={(e) => setControlCode(e.target.value)} /></label>
        <label>{t('m21.controlTesting.formName')}<input aria-label="Control name" value={controlName} onChange={(e) => setControlName(e.target.value)} /></label>
        <label>{t('m21.controlTesting.formType')}<input aria-label="Control type" value={controlType} onChange={(e) => setControlType(e.target.value)} /></label>
        <UserPicker label={t('m21.controlTesting.formOwner')} testId="control-owner" value={ownerId} onPick={(id) => setOwnerId(id)} />
        <label>{t('m21.controlTesting.formFrequency')}
          <select aria-label="Testing frequency" value={testingFrequency} onChange={(e) => setTestingFrequency(e.target.value)}>
            <option value="MONTHLY">MONTHLY</option>
            <option value="QUARTERLY">QUARTERLY</option>
            <option value="ANNUAL">ANNUAL</option>
          </select>
        </label>
        <button type="submit" disabled={busyId === 'create'}>{t('m21.controlTesting.createButton')}</button>
      </form>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="controls-table">
        <thead><tr><th>{t('m21.controlTesting.colCode')}</th><th>{t('m21.controlTesting.colName')}</th><th>{t('m21.controlTesting.colType')}</th><th>{t('m21.controlTesting.colFrequency')}</th><th>{t('m21.controlTesting.colEffectiveness')}</th><th>{t('m21.controlTesting.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.controlId}>
              <td>{c.controlCode}</td>
              <td>{c.controlName}</td>
              <td>{c.controlType}</td>
              <td>{c.testingFrequency}</td>
              <td>{c.effectiveness}</td>
              <td>
                <button
                  type="button"
                  aria-label={`test-pass-${c.controlId}`}
                  disabled={busyId === `test-${c.controlId}`}
                  onClick={() => recordTest(c.controlId, 'PASS')}
                >{t('m21.controlTesting.testPass')}</button>
                <button
                  type="button"
                  aria-label={`test-fail-${c.controlId}`}
                  disabled={busyId === `test-${c.controlId}`}
                  onClick={() => recordTest(c.controlId, 'FAIL')}
                >{t('m21.controlTesting.testFail')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
